/**
 * window.DeeplySmart — the host surface runtime-loaded plugin bundles bind to.
 *
 * Plugin bundles are built with react / react-dom / react/jsx-runtime /
 * @tanstack/react-query / wouter / @deeplysmart/ui / @deeplysmart/comms
 * shimmed to these globals,
 * which guarantees a single React instance (hooks work), a shared TanStack
 * Query client/context, and a shared wouter router.
 *
 * This module MUST be imported first in src/main.tsx, before anything renders,
 * so the surface exists before any plugin script can load.
 */
import * as React from "react";
import * as ReactDOM from "react-dom";
import * as ReactDOMClient from "react-dom/client";
import * as jsxRuntime from "react/jsx-runtime";
import * as ReactQuery from "@tanstack/react-query";
import * as wouter from "wouter";
import { HOST_API_VERSION } from "@shared/pluginManifest";
import * as ui from "./hostUi";
import * as comms from "./hostComms";
// Dependency-free leaf module (constants + pure helpers) — safe to import in
// this file, which main.tsx loads before anything renders.
import { GOOGLE_CONFIG } from "@/lib/googleAuth";
import type { PluginRegistration } from "./types";

type RegistrationListener = (pluginId: string, registration: PluginRegistration) => void;

const registrations = new Map<string, PluginRegistration>();
const listeners = new Set<RegistrationListener>();

function registerPlugin(pluginId: string, registration: PluginRegistration): void {
  if (!pluginId || typeof pluginId !== "string") {
    console.error("[plugins] registerPlugin called without a plugin id");
    return;
  }
  if (!registration || typeof registration !== "object" || !registration.pages) {
    console.error(`[plugins] registerPlugin("${pluginId}") called without pages`);
    return;
  }
  registrations.set(pluginId, registration);
  for (const listener of listeners) {
    try {
      listener(pluginId, registration);
    } catch (e) {
      console.error(`[plugins] registration listener failed for "${pluginId}"`, e);
    }
  }
}

/** Host-internal: read a registration captured from an executed bundle. */
export function getRegistration(pluginId: string): PluginRegistration | undefined {
  return registrations.get(pluginId);
}

/** Host-internal: drop a registration (deactivate/uninstall). */
export function dropRegistration(pluginId: string): void {
  registrations.delete(pluginId);
}

/** Host-internal: subscribe to registerPlugin calls. Returns unsubscribe. */
export function onPluginRegistered(listener: RegistrationListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Public, non-secret host configuration plugins may read. Values come from the
 * host's Vite env at build time. Only PUBLIC identifiers belong here — never
 * API keys or secrets (this object is readable by every loaded bundle).
 */
export interface DeeplySmartHostConfig {
  /**
   * The host's Google OAuth 2.0 Web client ID — the same value the host's own
   * Google features use (GOOGLE_CONFIG.clientId), or null when unset. Lets Google-backed plugins (e.g. Gmail) run the GIS token flow
   * against the same client the host uses, without asking each user to paste it.
   */
  googleClientId: string | null;
}

// Resolved from GOOGLE_CONFIG rather than import.meta.env directly, so plugins
// get exactly the client the host's own Google features use — including its
// fallback. Reading the env var here independently meant that in a build
// environment without VITE_GOOGLE_CLIENT_ID the host kept working (via that
// fallback) while plugins were handed null and showed "Google OAuth isn't
// configured". One source of truth prevents that split.
const hostConfig: DeeplySmartHostConfig = Object.freeze({
  googleClientId: GOOGLE_CONFIG.clientId.trim() || null,
});

export interface DeeplySmartGlobal {
  hostApiVersion: number;
  hostConfig: DeeplySmartHostConfig;
  React: typeof React;
  ReactDOM: typeof ReactDOM;
  ReactDOMClient: typeof ReactDOMClient;
  jsxRuntime: typeof jsxRuntime;
  ReactQuery: typeof ReactQuery;
  wouter: typeof wouter;
  ui: typeof ui;
  /** Shared communications UI — see docs/plugin-sdk/deeplysmart-comms.d.ts. */
  comms: typeof comms;
  registerPlugin: typeof registerPlugin;
}

declare global {
  interface Window {
    DeeplySmart?: DeeplySmartGlobal;
  }
}

if (typeof window !== "undefined" && !window.DeeplySmart) {
  window.DeeplySmart = Object.freeze({
    hostApiVersion: HOST_API_VERSION,
    hostConfig,
    React,
    ReactDOM,
    ReactDOMClient,
    jsxRuntime,
    ReactQuery,
    wouter,
    ui,
    comms,
    registerPlugin,
  });
}
